import React from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { Box, Alert, AlertIcon, AlertTitle, AlertDescription, Link, Text } from '@chakra-ui/react';

// Get Stripe publishable key from environment variables
// Use test key for development and live key for production
const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

// Check if the key is available and log the status
if (!stripePublishableKey) {
  console.error('Missing Stripe publishable key. Add VITE_STRIPE_PUBLISHABLE_KEY to your .env.local file');
  console.log('Current environment variables available:', 
    Object.keys(import.meta.env)
      .filter(key => key.startsWith('VITE_'))
      .reduce((obj, key) => {
        obj[key] = key.includes('KEY') ? '[REDACTED]' : import.meta.env[key];
        return obj;
      }, {})
  );
}

// Log key format for debugging (without revealing the actual key)
if (stripePublishableKey) {
  console.log('Stripe key format check:', {
    starts_with_pk: stripePublishableKey.startsWith('pk_'),
    is_test_key: stripePublishableKey.startsWith('pk_test_'),
    is_live_key: stripePublishableKey.startsWith('pk_live_'),
    char_count: stripePublishableKey.length,
  });
}

const stripePromise = loadStripe(stripePublishableKey);

// In development mode, show info about test card numbers
const TestModeInfo = () => {
  if (process.env.NODE_ENV !== 'development') return null;
  
  // Detect if we're using a test key (starts with pk_test)
  const isTestMode = stripePublishableKey?.startsWith('pk_test_');
  
  if (!isTestMode) return null;
  
  return (
    <Alert status="info" mb={4} fontSize="sm">
      <AlertIcon />
      <Box>
        <AlertTitle>Test Mode</AlertTitle>
        <AlertDescription>
          <Text mb={2}>
            Use these test card numbers:
          </Text>
          <Text>Success: 4242 4242 4242 4242</Text>
          <Text>Decline: 4000 0000 0000 0002</Text>
          <Text mb={2}>Use any future date, any 3 digits for CVC, and any ZIP.</Text>
          <Link href="https://stripe.com/docs/testing" isExternal color="blue.500">
            More test cards →
          </Link>
        </AlertDescription>
      </Box>
    </Alert>
  );
};

const StripeWrapper = ({ children }) => {
  const stripeOptions = {
    // Pass client locale or other options to the Elements instance
    locale: 'en',
    fonts: [
      {
        cssSrc: 'https://fonts.googleapis.com/css?family=Outfit:300,400,500&display=swap',
      },
    ],
  };
  
  return (
    <>
      <TestModeInfo />
      <Elements stripe={stripePromise} options={stripeOptions}>
        {children}
      </Elements>
    </>
  );
};

export default StripeWrapper; 